import { useState, useEffect } from "react";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  selectItems,
  selectItem,
  selectUpdateStatus,
  selectGetStatus,
  selectAddStatus,
} from "src/modules/spItems/selectors";
import { updateItem, addItem, getItems } from "src/modules/spItems/actions";
import { Comment } from "src/components/ui-icons/card";
import _ from "lodash";
import HistoryStatuses from "../historyStatuses/historyStatusesMobile";
import { getHistoryTimelineColorMobile } from "../historyStatuses/statusTimelineMobile";
import HistoryFieldChanges from "../historyFieldChanges";
import PeoplePicker from "src/components/ui/form/peoplePicker";
import Button from "src/components/Button";
import Loading from "src/components/Loading";
import { UserAvatar } from "src/components/ui/avatar/Avatar";
import { deleteItemRequest, getItemsRequest } from "src/modules/spItems/api";
import moment from "moment";
import {
  approversCardDocumentListName,
  approversDependenciesListName,
  approversListName,
  emailsSender,
  historyByDocumentListName,
  historyChangeFieldsDocumentListName,
  myDocumentsListName,
  paramsListName,
  siteUrl,
  versionsByDocumentListName,
} from "src/api/config";
import { emailAPI } from "src/utils/api.utils";
import { addUsers } from "src/modules/users/actions";
import { notificationsListName } from "src/api/config/notifications.config";
import { RootState } from "src/store/rootReducer";
import { emailQueueListName } from "src/api/config/email.config";
import { docStatuses, notificationStatuses } from "src/api/config/systemSwitch";
const { sendEmail } = emailAPI();

const getLookupId = (value: any): number | undefined => {
  if (value == null) return undefined;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const num = Number(value);
    if (!Number.isNaN(num)) return num;
  }
  if (typeof value.get_lookupId === "function") return value.get_lookupId();
  if (typeof value.ID === "number") return value.ID;
  if (typeof value.Id === "number") return value.Id;
  return undefined;
};

const getInitials = (displayName?: string) => {
  if (!displayName) return undefined;
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? "";
  const initials = `${first}${second}`.toUpperCase();
  return initials || undefined;
};

const formatDateTime = (value: any) => {
  if (!value) return "";
  if (typeof value.format === "function") return value.format("HH:mm DD.MM.YYYY");
  return moment(value).format("HH:mm DD.MM.YYYY");
};

const unixTime = (value: any) => {
  if (!value) return 0;
  if (typeof value.unix === "function") return value.unix();
  return moment(value).unix();
};

export const getLatestUniqueGroups = (arr: any[]) => {
  const map = new Map();

  for (const item of arr) {
    const key =
      (item.ApprovalGroup && item.ApprovalGroup.ID) ||
      (item.ApprovalGroup && item.ApprovalGroup.get_lookupId?.());

    if (key) {
      const existing = map.get(key);

      if (!existing || new Date(item.Created) > new Date(existing.Created)) {
        map.set(key, item);
      }
    }
  }

  return [...map.values()];
};

type Props = {
  versionView: any;
  documentCardID: any;
};

const ApproversCard = (props: Props) => {
  const { versionView, documentCardID } = props;
  const statusGetHistory = useAppSelector((state) =>
    selectGetStatus(state, historyByDocumentListName)
  );
  const historyCard = useAppSelector((state) =>
    selectItems(state, historyByDocumentListName)
  );
  const users: any = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();
  const card: any = useAppSelector((state) =>
    selectItem(
      state,
      (versionView && versionsByDocumentListName) || myDocumentsListName
    )
  );
  const [edit, setEdit] = useState(false);
  const [currentApprovers, setCurrentApprovers] = useState<any>({});
  const [newApprovers, setNewApprovers] = useState<any>({});
  const currentUser = useAppSelector(selectCurrentUser);
  const approversByDocument: any = useAppSelector((state) =>
    selectItems(state, approversCardDocumentListName)
  );
  const [showSaveBtns, setShowSaveBtns] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const statusUpdateHistory = useAppSelector((state) =>
    selectUpdateStatus(state, historyByDocumentListName)
  );
  const statusAddHistoryCard = useAppSelector((state) =>
    selectAddStatus(state, historyChangeFieldsDocumentListName)
  );
  const statusUpdateApprovers = useAppSelector((state) =>
    selectUpdateStatus(state, approversCardDocumentListName)
  );
  const statusGetApprovers = useAppSelector((state) =>
    selectGetStatus(state, approversCardDocumentListName)
  );
  const [approversDependencies, setApproversDependencies] = useState<any>([]);
  const repeatEmails: any = useAppSelector((state) =>
    selectItems(state, paramsListName)
  );
  const statusUsers: string = useAppSelector(
    (state: RootState) => state.users.status
  );
  const approversList = useAppSelector((state) =>
    selectItems(state, approversListName)
  );
  const [renderHistoryData, setRenderHistoryData] = useState<any>([]);

  useEffect(() => {
    dispatch(
      getItems({
        listName: historyByDocumentListName,
        filters: {
          DocumentID: versionView || documentCardID,
          VersionDocument: (versionView && true) || undefined,
        },
        sortOrder: { name: "ID", asc: true },
      })
    );
  }, [dispatch, documentCardID, versionView]);

  useEffect(() => {
    if (!_.isEmpty(card)) {
      dispatch(
        getItems({
          listName: approversCardDocumentListName,
          filters: {
            Document: card.ID,
            VersionDocument: (versionView && true) || undefined,
          },
        })
      );
    }
  }, [dispatch, card, versionView]);

  useEffect(() => {
    if (Object.keys(card).length > 0 && approversDependencies.length === 0) {
      const docTypeId = getLookupId(card?.DocType);
      const collectionId = getLookupId(card?.Collection);
      if (docTypeId == null || collectionId == null) return;
      getItemsRequest({
        listName: approversDependenciesListName,
        filters: { DocType: docTypeId, Collection: collectionId },
        sortOrder: { name: "Order0", asc: true },
      }).then((results: any) => {
        if (results.items.length > 0) {
          let usersEmail: any = [];
          // @ts-ignore
          results.items.forEach((item: any) => {
            Object.keys(item).forEach((key) => {
              const field = item[key];
              if (field && !Array.isArray(field) && field.get_email) {
                if (field.get_email()) {
                  usersEmail = [...usersEmail, field.get_email().toLowerCase()];
                }
              }
              if (
                field &&
                Array.isArray(field) &&
                field.length > 0 &&
                field[0].get_email
              ) {
                field.forEach((user) => {
                  if (user.get_email()) {
                    usersEmail = [
                      ...usersEmail,
                      user.get_email().toLowerCase(),
                    ];
                  }
                });
              }
            });
          });
          usersEmail = _.uniqWith(usersEmail, _.isEqual);
          if (usersEmail.length > 0) {
            let actualEmails: any = [];
            usersEmail.forEach((item: string) => {
              let check = false;
              Object.keys(users).forEach((key) => {
                if (key === item) {
                  check = true;
                }
              });
              if (!check) {
                actualEmails = [...actualEmails, item.toLowerCase()];
              }
            });
            if (actualEmails) {
              dispatch(addUsers(actualEmails));
            }
          }
        }
        setApproversDependencies([...results.items]);
      });
    }
  }, [card, approversDependencies]);

  useEffect(() => {
    if (
      Object.keys(card).length > 0 &&
      getLookupId(card?.Status) === docStatuses.approval &&
      approversByDocument.length > 0 &&
      historyCard.length > 0
    ) {
      //Изменение согласующих доступно авторам, согласовантам и админам при статусе документа "На согласовании"
      if (
        currentUser.isAdmin ||
        (currentUser.email &&
          currentUser.email.toLowerCase() === card.AuthorEmail.toLowerCase())
      ) {
        setEdit(true);
      } else {
        let flagEdit = false;
        approversByDocument.forEach((element: any) => {
          if (
            element.Approvers.some(
              (item: any) =>
                item.Email.toLowerCase() === currentUser.email.toLowerCase()
            )
          ) {
            flagEdit = true;
          }
        });
        if (flagEdit) {
          setEdit(true);
        } else {
          setEdit(false);
        }
      }
    }
  }, [card, currentUser, approversByDocument, historyCard]);

  useEffect(() => {
    if (Object.keys(newApprovers).length > 0) {
      setShowSaveBtns(true);
    }
  }, [newApprovers]);

  const changesApproversHandle = (approvers: []) => {
    if (approvers && approvers.length > 0) {
      let objApprovers: any = {};
      approvers.forEach((approver: any) => {
        if (!objApprovers.hasOwnProperty(approver.code)) {
          objApprovers[approver.code] = [];
        }
        objApprovers[approver.code] = [
          ...objApprovers[approver.code],
          approver,
        ];
      });
      Object.keys(objApprovers).forEach((key: string) => {
        setNewApprovers((prevState: any) => {
          return {
            ...prevState,
            [key]: approvers,
          };
        });
      });
    }
  };

  useEffect(() => {
    if (
      Object.keys(card).length > 0 &&
      approversDependencies.length > 0 &&
      historyCard.length > 0
    ) {
      historyCard.forEach((history: any) => {
        const statuses = Array.isArray(history?.Statuses) ? history.Statuses : [];
        const approvalGroupId = getLookupId(history?.ApprovalGroup);
        if (
          statuses.filter(
            (status: any) => getLookupId(status) === docStatuses.waitApproval
          ).length === 1 &&
          approvalGroupId != null
        ) {
          const approversRowDependencies: any = _.orderBy(
            approversDependencies,
            ["Order0"],
            ["asc"]
          ).filter(
            (approvers: any) =>
              approvers.Group.get_lookupId() === approvalGroupId
          )[0];
          setCurrentApprovers((prevState: any) => {
            return {
              ...prevState,
              [approvalGroupId]: {
                field: {
                  dataField: {
                    single: false,
                    options:
                      approversRowDependencies &&
                      approversRowDependencies.Users.length > 0 &&
                      approversRowDependencies.Users.map((item: any) => {
                        const email = item.get_email().toLowerCase();
                        return {
                          value: users[email] && users[email].email,
                          label: users[email] && users[email].displayName,
                          avatar: users[email] && users[email].avatar,
                          email: users[email] && users[email].email,
                            code: approvalGroupId,
                          disabled: currentUser.email.toLowerCase() === email,
                        };
                      }),
                    disabled:
                      approversRowDependencies &&
                      approversRowDependencies.NotChange,
                    width: "w-full",
                    placeholder: "Введите имя или фамилию",
                  },
                  onChange: changesApproversHandle,
                  value:
                    (approversRowDependencies &&
                      approversRowDependencies.NotChange &&
                      approversRowDependencies.DefaultApprovers &&
                      approversRowDependencies.DefaultApprovers.length > 0 &&
                      approversRowDependencies.DefaultApprovers.map(
                        (item: any) => {
                          const email = item.get_email().toLowerCase();
                          return {
                            value: users[email] && users[email].email,
                            label: users[email] && users[email].displayName,
                            avatar: users[email] && users[email].avatar,
                            email: users[email] && users[email].email,
                            code: approvalGroupId,
                          };
                        }
                      )) ||
                    (history.Approvers &&
                      history.Approvers.map((item: any) => {
                        return {
                          value: users[item.Email] && users[item.Email].email,
                          label:
                            users[item.Email] && users[item.Email].displayName,
                          avatar: users[item.Email] && users[item.Email].avatar,
                          email: users[item.Email] && users[item.Email].email,
                          code: approvalGroupId,
                        };
                      })) ||
                    [],
                  type: "history",
                },
              },
            };
          });
        }
      });
    }
  }, [historyCard, approversDependencies, card, users]);

  const resetDefaultValuesHandle = () => {
    setNewApprovers({});
    setCurrentApprovers({});
    setApproversDependencies([]);
    setShowSaveBtns(false);
  };

  const saveNewApproversHandle = () => {
    let updateHistory: any = [],
      updateApprovers: any = [],
      dataNotificationCard: any = [],
      oldApproversForHistory: any = [],
      newApproversForHistory: any = [],
      newApproversTemp: any = { ...newApprovers };
    setCustomLoading(true);
    historyCard.forEach((history: any) => {
      if (
        history.Statuses.filter(
          (status: any) => status.ID === docStatuses.waitApproval
        ).length === 1
      ) {
        if (
          newApproversTemp[history.ApprovalGroup.ID] &&
          Array.isArray(newApproversTemp[history.ApprovalGroup.ID]) &&
          newApproversTemp[history.ApprovalGroup.ID].length > 0
        ) {
          oldApproversForHistory = [
            ...oldApproversForHistory,
            ...history.Approvers.map((user: any) =>
              user.Email.toLowerCase().trim()
            ),
          ];
          newApproversForHistory = [
            ...newApproversForHistory,
            ...newApproversTemp[history.ApprovalGroup.ID].map(
              (user: any) => user.value
            ),
          ];
          updateHistory = [
            ...updateHistory,
            {
              ID: history.ID,
              Approvers: newApproversTemp[history.ApprovalGroup.ID].map(
                (user: any) => user.value
              ),
            },
          ];
        }
      }
    });

    if (updateHistory.length > 0) {
      dispatch(
        updateItem({
          listName: historyByDocumentListName,
          body: updateHistory[0],
          bodyArr: updateHistory,
        })
      );
    }

    const changesFieldValue = {
      ApproverChange: {
        old: oldApproversForHistory,
        new: newApproversForHistory,
      },
    };

    const historyFieldsCardAdd = {
      Title: `History fields card # ${card.ID}`,
      DocumentID: card.ID,
      ChangeDate: new Date(),
      JSONChangesFields: JSON.stringify(changesFieldValue),
      Statuses: [docStatuses.edited], // Изменён
      VersionDocument: (versionView && true) || false,
    };

    dispatch(
      addItem({
        listName: historyChangeFieldsDocumentListName,
        body: historyFieldsCardAdd,
      })
    );

    approversByDocument.forEach((approvers: any) => {
      if (
        approvers.Statuses.filter(
          (status: any) => status.ID === docStatuses.waitApproval
        ).length === 1
      ) {
        if (
          newApproversTemp[approvers.Group.ID] &&
          Array.isArray(newApproversTemp[approvers.Group.ID]) &&
          newApproversTemp[approvers.Group.ID].length > 0
        ) {
          if (approvers.Queue) {
            newApproversTemp[approvers.Group.ID]
              .map((user: any) => user.value)
              .forEach((email: any) => {
                dataNotificationCard = [
                  ...dataNotificationCard,
                  {
                    Title:
                      (versionView && `Согласовать версию # ${card.ID}`) ||
                      `Согласовать # ${card.ID}`,
                    ReviewDate: moment(new Date())
                      .add(
                        Number(
                          repeatEmails
                            .find(
                              (param: any) =>
                                param.Key === "documentsApproveDays"
                            )
                            ["Value"].trim()
                        ),
                        "days"
                      )
                      .toDate(),
                    AuthorComment: "",
                    DocumentCode: `${card.Code}.${card.Index}`,
                    Document: (versionView && card.MainDocument.ID) || card.ID,
                    NotificationType: notificationStatuses.approval, // Согласовать
                    Employee: email.toLowerCase(),
                    VersionDocument: versionView,
                    EmployeeEmail: email.toLowerCase(),
                    AuthorEmail: currentUser.email.toLowerCase(),
                    Completed: new Date(),
                  },
                ];
              });
            if (emailsSender) {
              getItemsRequest({
                listName: emailQueueListName,
                filters: {
                  CheckDoc:
                    (versionView && String(card.MainDocument.ID)) ||
                    String(card.ID),
                },
              }).then((results) => {
                const emails = [...results.items];
                const emailsIDs: { ID: number }[] = emails.map((email) => {
                  return { ID: email.ID };
                });
                deleteItemRequest({
                  listName: emailQueueListName,
                  Ids: emailsIDs,
                  ID: 0,
                }).then(() => {
                  let emailsHanleArr: any = [];
                  const minutes = Number(
                    repeatEmails
                      .find((param: any) => param.Key === "documentApprove")
                      ["Value"].trim()
                  );
                  const dateSendEmailsArr = [
                    moment(new Date()).clone().subtract(12, "hours").toDate(),
                    moment(new Date()).clone().add(minutes, "m").toDate(),
                  ];
                  dateSendEmailsArr.forEach((date: Date, idxDate: number) => {
                    newApproversTemp[approvers.Group.ID]
                      .map((user: any) => user.value)
                      .forEach((email: any) => {
                        const emailBody = {
                          Body: `Здравствуйте ${
                            users[email.toLowerCase()] &&
                            users[email.toLowerCase()]["displayName"]
                          },<br/><br/>Прочитайте следующий документ и подтвердите факт его согласования:<br/><br/><a href="${siteUrl}/dashboard/1?documentCard=${
                            (versionView && card.MainDocument.ID) || card.ID
                          }${
                            (versionView && `&version=${versionView}`) || ""
                          }">${
                            card.Title
                          }</a><br/><br/>Срок согласования: <span${
                            (idxDate === 1 && ` style="color:red;"`) || ""
                          }>${moment(new Date())
                            .add(
                              Number(
                                repeatEmails
                                  .find(
                                    (param: any) =>
                                      param.Key === "documentsApproveDays"
                                  )
                                  ["Value"].trim()
                              ),
                              "days"
                            )
                            .format("DD.MM.YYYY")}</span>`,
                          DateToBeSentAt: date,
                          Title: "Мои Документы | Согласование документа",
                          Subject: "Мои Документы | Согласование документа",
                          Recepients: email,
                          CheckDoc:
                            (versionView && String(card.MainDocument.ID)) ||
                            String(card.ID),
                          RepeatAt: (idxDate === 1 && minutes) || 0,
                        };
                        emailsHanleArr = [
                          ...emailsHanleArr,
                          sendEmail(emailBody),
                        ];
                      });
                  });
                  Promise.all(emailsHanleArr);
                });
              });
            }
          }
          updateApprovers = [
            ...updateApprovers,
            {
              ID: approvers.ID,
              Approvers: newApproversTemp[approvers.Group.ID].map(
                (user: any) => user.value
              ),
            },
          ];
        }
      }
    });
    if (updateApprovers.length > 0) {
      dispatch(
        updateItem({
          listName: approversCardDocumentListName,
          body: updateApprovers[0],
          bodyArr: updateApprovers,
        })
      );
    }
    if (dataNotificationCard.length > 0) {
      dispatch(
        addItem({
          listName: notificationsListName,
          body: dataNotificationCard[0],
          bodyArr: dataNotificationCard,
        })
      );
    }
  };

  useEffect(() => {
    if (
      statusUpdateHistory === "SUCCESS" &&
      statusUpdateApprovers === "SUCCESS" &&
      statusAddHistoryCard === "SUCCESS"
    ) {
      setNewApprovers({});
      setCurrentApprovers({});
      setApproversDependencies([]);
      setShowSaveBtns(false);
      setCustomLoading(false);
    }
  }, [statusUpdateHistory, statusUpdateApprovers, statusAddHistoryCard]);

  useEffect(() => {
    let tempHistoryCard: any = [...historyCard],
      tempHistoryCardUpdateOrder: any = [];
    tempHistoryCardUpdateOrder = tempHistoryCard
      .map((item: any) => {
        const approvalGroupId = getLookupId(item?.ApprovalGroup);
        const order =
          (approvalGroupId != null &&
            approversList.filter((group) => group.ID === approvalGroupId)
              .length > 0 &&
            approversList.filter(
              (group) => group.ID === approvalGroupId
            )[0]["SortColumn"]) ||
          0;
        return { ...item, order };
      })
      .sort(function (a: any, b: any) {
        return a.order - b.order;
      });
    setRenderHistoryData([...tempHistoryCardUpdateOrder]);
  }, [historyCard]);

  const cardStatusId = getLookupId(card?.Status);
  const isPublicOrReview =
    cardStatusId === docStatuses.public || cardStatusId === docStatuses.review;

  return (
    <div className="flex-1 bg-white rounded-lg shadow-xl">
      {((customLoading ||
        statusUpdateHistory === "LOADING" ||
        statusUpdateApprovers === "LOADING" ||
        statusGetHistory === "LOADING" ||
        statusGetApprovers === "LOADING" ||
        statusAddHistoryCard === "LOADING" ||
        statusUsers === "LOADING") && <Loading />) || (
        <>
          <div className="relative px-0">
            {statusGetHistory !== "LOADING" && historyCard.length === 0 && (
              <div className="py-8 text-sm text-[#637381]">Нет данных</div>
            )}
            {((isPublicOrReview &&
              historyCard.filter((history) => history.Approved).length > 1) ||
              (!isPublicOrReview &&
                historyCard.length > 1)) && (
              <div className="absolute left-[14.5px] h-full border border-solid border-[#DFE4EA] hidden sm:block"></div>
            )}
            {(isPublicOrReview && (
              <>
                {renderHistoryData.length > 0 &&
                  getLatestUniqueGroups(renderHistoryData).map(
                    (history: any) => (
                      <>
                        {history.Approved && (
                          <div className="history_block flex flex-col w-full my-6 -ml-1.5">
                            <div className="pt-5 flex items-start z-10 mb-2">
                              <div
                                className="w-2.5 h-2.5 rounded-full mr-4 mt-1.5"
                                style={{
                                  backgroundColor: getHistoryTimelineColorMobile(
                                    history.Statuses
                                  ),
                                }}
                              ></div>
                              <span className="text-[#637381] text-sm whitespace-nowrap">
                                {formatDateTime(history.ChangeDate || history.Created)}
                              </span>
                            </div>
                            <div className="flex p-5 flex-col justify-center items-start gap-5 flex-[1_0%_0%] rounded-lg border border-solid border-[#DFE4EA] w-full">
                              <div className="w-full">
                                <span className="text-sm block mb-[19px]">
                                  {(history.ChangeRights && history.Title) ||
                                    history.ApprovalGroup?.Title}
                                </span>
                                <HistoryStatuses statuses={history.Statuses} />
                              </div>
                              <div className="flex items-center gap-2 w-full">
                                <UserAvatar
                                  avatar={
                                    users[history.Approved["Email"]]?.avatar ??
                                    null
                                  }
                                  shortName={
                                    getInitials(
                                      users[history.Approved["Email"]]
                                        ?.displayName
                                    ) ?? ""
                                  }
                                  displayName={
                                    users[history.Approved["Email"]]
                                      ?.displayName || ""
                                  }
                                  email={history.Approved["Email"] || ""}
                                />
                                <div className="flex flex-col justify-center items-start">
                                  <span className="text-sm text-[#111928]">
                                    {users[history.Approved["Email"]] &&
                                      users[history.Approved["Email"]]
                                        .displayName}
                                  </span>
                                  <span
                                    className="text-sm text-[#637381]"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        users[history.Approved["Email"]] &&
                                        users[history.Approved["Email"]][
                                          "jobTitle"
                                        ],
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )
                  )}
              </>
            )) || (
              <>
                {renderHistoryData.length > 0 &&
                  renderHistoryData
                    .filter((history: any) =>
                      Array.isArray(history?.Statuses) &&
                      history.Statuses.some(
                        (item: any) =>
                          getLookupId(item) !== docStatuses.waitApproval
                      )
                    )
                    .sort(
                      (a: any, b: any) => unixTime(a.Created) - unixTime(b.Created)
                    )
                    .map((history: any) => (
                      <div className="history_block flex flex-col w-full my-6 -ml-1.5">
                        <div className="pt-5 flex items-start z-10 mb-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full mr-4 mt-1.5"
                            style={{
                              backgroundColor: getHistoryTimelineColorMobile(
                                history.Statuses
                              ),
                            }}
                          ></div>
                          <span className="text-[#637381] text-sm whitespace-nowrap">
                            {formatDateTime(history.ChangeDate || history.Created)}
                          </span>
                        </div>
                        <div className="flex p-5 flex-col justify-center items-start gap-5 flex-[1_0%_0%] rounded-lg border border-solid border-[#DFE4EA] w-full">
                          <div className="w-full">
                            <span className="text-sm block mb-[19px]">
                              {(history.ChangeRights && history.Title) ||
                                history.ApprovalGroup?.Title}
                            </span>
                            <HistoryStatuses statuses={history.Statuses} />
                          </div>
                          <div className="flex items-start gap-2 w-full flex-wrap">
                            {(!history.Approved &&
                              !history.Denied &&
                              currentApprovers[getLookupId(history?.ApprovalGroup) as any] &&
                              edit &&
                              (Array.isArray(history?.Statuses)
                                ? history.Statuses.filter(
                                    (status: any) =>
                                      getLookupId(status) ===
                                      docStatuses.waitApproval
                                  ).length === 1
                                : false) && (
                                <>
                                  {history.Approvers &&
                                    history.Approvers.length > 0 && (
                                      <PeoplePicker
                                        dataField={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["dataField"]
                                        }
                                        onChange={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["onChange"]
                                        }
                                        value={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["value"]
                                        }
                                        type={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["type"]
                                        }
                                      />
                                    )}
                                </>
                              )) ||
                              (history.Approved && (
                                <>
                                  <UserAvatar
                                    avatar={
                                      users[history.Approved["Email"]] &&
                                      users[history.Approved["Email"]].avatar
                                    }
                                    shortName={
                                      getInitials(
                                        users[history.Approved["Email"]] &&
                                          users[history.Approved["Email"]]
                                            .displayName
                                      ) ?? ""
                                    }
                                    displayName={
                                      users[history.Approved["Email"]] &&
                                      users[history.Approved["Email"]]
                                        .displayName
                                    }
                                    email={history.Approved["Email"]}
                                  />
                                  <div className="flex flex-col justify-center items-start">
                                    <span className="text-sm text-[#111928]">
                                      {users[history.Approved["Email"]] &&
                                        users[history.Approved["Email"]]
                                          .displayName}
                                    </span>
                                    <span
                                      className="text-sm text-[#637381]"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          users[history.Approved["Email"]] &&
                                          users[history.Approved["Email"]][
                                            "jobTitle"
                                          ],
                                      }}
                                    />
                                  </div>
                                </>
                              )) ||
                              (history.Denied && (
                                <>
                                  <UserAvatar
                                    avatar={
                                      users[history.Denied["Email"]] &&
                                      users[history.Denied["Email"]].avatar
                                    }
                                    shortName={
                                      getInitials(
                                        users[history.Denied["Email"]] &&
                                          users[history.Denied["Email"]]
                                            .displayName
                                      ) ?? ""
                                    }
                                    displayName={
                                      users[history.Denied["Email"]] &&
                                      users[history.Denied["Email"]].displayName
                                    }
                                    email={history.Denied["Email"]}
                                  />
                                  <div className="flex flex-col justify-center items-start">
                                    <span className="text-sm text-[#111928]">
                                      {users[history.Denied["Email"]] &&
                                        users[history.Denied["Email"]]
                                          .displayName}
                                    </span>
                                    <span
                                      className="text-sm text-[#637381]"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          users[history.Denied["Email"]] &&
                                          users[history.Denied["Email"]][
                                            "jobTitle"
                                          ],
                                      }}
                                    />
                                  </div>
                                </>
                              )) || (
                                <>
                                  {history.Approvers.map((approver: any) => (
                                    <>
                                      <UserAvatar
                                        avatar={
                                          users[approver["Email"]] &&
                                          users[approver["Email"]].avatar
                                        }
                                        shortName={
                                          getInitials(
                                            users[approver["Email"]] &&
                                              users[approver["Email"]]
                                                .displayName
                                          ) ?? ""
                                        }
                                        displayName={
                                          users[approver["Email"]] &&
                                          users[approver["Email"]].displayName
                                        }
                                        email={approver["Email"]}
                                      />
                                      <div className="flex flex-col justify-center items-start">
                                        <span className="text-sm text-[#111928]">
                                          {users[approver["Email"]] &&
                                            users[approver["Email"]]
                                              .displayName}
                                        </span>
                                        <span
                                          className="text-sm text-[#637381]"
                                          dangerouslySetInnerHTML={{
                                            __html:
                                              users[approver["Email"]] &&
                                              users[approver["Email"]][
                                                "jobTitle"
                                              ],
                                          }}
                                        />
                                      </div>
                                    </>
                                  ))}
                                </>
                              )}
                          </div>
                          {history.Comment && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Comment />
                              <span className="text-sm text-[#111928]">
                                {history.Comment}
                              </span>
                            </div>
                          )}
                          <HistoryFieldChanges
                            versionView={versionView}
                            documentCardID={documentCardID}
                            JsomHistoryFields={history.JSONChangesFields}
                          />
                        </div>
                      </div>
                    ))}
                {renderHistoryData.length > 0 &&
                  renderHistoryData
                    .filter((history: any) =>
                      Array.isArray(history?.Statuses) &&
                      history.Statuses.some(
                        (item: any) =>
                          getLookupId(item) === docStatuses.waitApproval
                      )
                    )
                    .map((history: any) => (
                      <div className="history_block flex flex-col w-full my-6 -ml-1.5">
                        <div className="pt-5 flex items-start z-10 mb-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full mr-4 mt-1.5"
                            style={{
                              backgroundColor: getHistoryTimelineColorMobile(
                                history.Statuses
                              ),
                            }}
                          ></div>
                          <span className="text-[#637381] text-sm whitespace-nowrap">
                            Ожидается
                          </span>
                        </div>
                        <div className="flex p-5 flex-col justify-center items-start gap-5 flex-[1_0%_0%] rounded-lg border border-solid border-[#DFE4EA] w-full">
                          <div className="w-full">
                            <span className="text-sm block mb-[19px]">
                              {(history.ChangeRights && history.Title) ||
                                history.ApprovalGroup?.Title}
                            </span>
                            <HistoryStatuses statuses={history.Statuses} />
                          </div>
                          <div className="flex items-start gap-2 w-full flex-wrap">
                            {(!history.Approved &&
                              !history.Denied &&
                              currentApprovers[
                                getLookupId(history?.ApprovalGroup) as any
                              ] &&
                              edit &&
                              (Array.isArray(history?.Statuses)
                                ? history.Statuses.filter(
                                    (status: any) =>
                                      getLookupId(status) ===
                                      docStatuses.waitApproval
                                  ).length === 1
                                : false) && (
                                <>
                                  {history.Approvers &&
                                    history.Approvers.length > 0 && (
                                      <PeoplePicker
                                        dataField={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["dataField"]
                                        }
                                        onChange={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["onChange"]
                                        }
                                        value={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["value"]
                                        }
                                        type={
                                          currentApprovers[
                                            getLookupId(
                                              history?.ApprovalGroup
                                            ) as any
                                          ]["field"]["type"]
                                        }
                                      />
                                    )}
                                </>
                              )) ||
                              (history.Approved && (
                                <>
                                  <UserAvatar
                                    avatar={
                                      users[history.Approved["Email"]] &&
                                      users[history.Approved["Email"]].avatar
                                    }
                                    shortName={
                                      getInitials(
                                        users[history.Approved["Email"]] &&
                                          users[history.Approved["Email"]]
                                            .displayName
                                      ) ?? ""
                                    }
                                    displayName={
                                      users[history.Approved["Email"]] &&
                                      users[history.Approved["Email"]]
                                        .displayName
                                    }
                                    email={history.Approved["Email"]}
                                  />
                                  <div className="flex flex-col justify-center items-start">
                                    <span className="text-sm text-[#111928]">
                                      {users[history.Approved["Email"]] &&
                                        users[history.Approved["Email"]]
                                          .displayName}
                                    </span>
                                    <span
                                      className="text-sm text-[#637381]"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          users[history.Approved["Email"]] &&
                                          users[history.Approved["Email"]][
                                            "jobTitle"
                                          ],
                                      }}
                                    />
                                  </div>
                                </>
                              )) ||
                              (history.Denied && (
                                <>
                                  <UserAvatar
                                    avatar={
                                      users[history.Denied["Email"]] &&
                                      users[history.Denied["Email"]].avatar
                                    }
                                    shortName={
                                      getInitials(
                                        users[history.Denied["Email"]] &&
                                          users[history.Denied["Email"]]
                                            .displayName
                                      ) ?? ""
                                    }
                                    displayName={
                                      users[history.Denied["Email"]] &&
                                      users[history.Denied["Email"]].displayName
                                    }
                                    email={history.Denied["Email"]}
                                  />
                                  <div className="flex flex-col justify-center items-start">
                                    <span className="text-sm text-[#111928]">
                                      {users[history.Denied["Email"]] &&
                                        users[history.Denied["Email"]]
                                          .displayName}
                                    </span>
                                    <span
                                      className="text-sm text-[#637381]"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          users[history.Denied["Email"]] &&
                                          users[history.Denied["Email"]][
                                            "jobTitle"
                                          ],
                                      }}
                                    />
                                  </div>
                                </>
                              )) || (
                                <>
                                  {history.Approvers.map((approver: any) => (
                                    <>
                                      <UserAvatar
                                        avatar={
                                          users[approver["Email"]] &&
                                          users[approver["Email"]].avatar
                                        }
                                        shortName={
                                          getInitials(
                                            users[approver["Email"]] &&
                                              users[approver["Email"]]
                                                .displayName
                                          ) ?? ""
                                        }
                                        displayName={
                                          users[approver["Email"]] &&
                                          users[approver["Email"]].displayName
                                        }
                                        email={approver["Email"]}
                                      />
                                      <div className="flex flex-col justify-center items-start">
                                        <span className="text-sm text-[#111928]">
                                          {users[approver["Email"]] &&
                                            users[approver["Email"]]
                                              .displayName}
                                        </span>
                                        <span
                                          className="text-sm text-[#637381]"
                                          dangerouslySetInnerHTML={{
                                            __html:
                                              users[approver["Email"]] &&
                                              users[approver["Email"]][
                                                "jobTitle"
                                              ],
                                          }}
                                        />
                                      </div>
                                    </>
                                  ))}
                                </>
                              )}
                          </div>
                          {history.Comment && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Comment />
                              <span className="text-sm text-[#111928]">
                                {history.Comment}
                              </span>
                            </div>
                          )}
                          <HistoryFieldChanges
                            versionView={versionView}
                            documentCardID={documentCardID}
                            JsomHistoryFields={history.JSONChangesFields}
                          />
                        </div>
                      </div>
                    ))}
              </>
            )}
          </div>
          {showSaveBtns && (
            <div className="w-full flex justify-between">
              <Button
                classBg="btn-white"
                shadow={true}
                text="Отменить изменения"
                customFunction={resetDefaultValuesHandle}
              />

              <Button
                classBg="btn-dark"
                text="Сохранить изменения"
                customFunction={saveNewApproversHandle}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApproversCard;
